<template>
  <!-- @TODO remove branches and use computed properties -->
  <template v-if="shouldShow">
    <RatingStars :rating="ratingAverage" :singleStar="compactFormat === 'single_star_average_and_count' && isCompact" :type="ratingToShow" @click="onClick" :class="starClasses" :size="size" />
    <template v-if="isCompact">
      <div v-if="compactFormat === 'count'" class="gmf-text-small gmf-product-rating-label gmf-product-rating-label-compact">
        ({{ ratingCount }})
      </div>
      <div v-else-if="compactFormat === 'average'" class="gmf-text-small gmf-product-rating-label gmf-product-rating-label-compact">
          {{ ratingAverage.toFixed(1) }}/5
      </div>
      <div v-else-if="compactFormat === 'single_star_average_and_count'" class="gmf-text-small gmf-product-rating-label gmf-product-rating-label-compact">
        <b>{{ ratingAverage.toFixed(1) }}</b> ({{ ratingCount }})
      </div>
      <div v-else-if="compactFormat === 'average_and_count'" class="gmf-text-small gmf-product-rating-label gmf-product-rating-label-compact">
        {{ ratingAverage.toFixed(1) }}/5 <b>({{ ratingCount }})</b>
      </div>
      <!-- or none -->
    </template>
    <div class="gmf-text-small gmf-product-rating-label" v-else>{{ ratingCount }} {{ ratingName }}</div>
  </template>
</template>

<style>
.gmf-product-rating:has(.gmf-rating-stars) {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--gmf-p-1);
}
.gmf-product-rating-label-compact b {
  color: var(--gmf-col-nearblack);
}
</style>

<script>
import state from '@/state';
import combineRatings from '@/utils/combineRatings';
import getProductRatings from '@/utils/getProductRatings';
import RatingStars from '@/components/RatingStars';
import openCommentSectionModal from '@/components/CommentSectionModal';

const t = state.t.prefix('product');

// This component can now combine ratings, just send multiple ids like AAA123+BBB456

export default {
  components: {
    RatingStars,
  },
  name: 'ProductRating',
  props: {
    productId: {
      type: String,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    mockJson: {
      type: String,
      default: null,
    },
    disableClick: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'medium',
      validator (value) {
        return ['small', 'medium', 'large'].includes(value);
      },
    },
  },
  data () {
    return {
      hasLoaded: false,
      allRatings: [],
    };
  },
  methods: {
    t,
    onClick (evt) {
      if (this.popupReviewModalEnabled) {
        openCommentSectionModal(this.productId);
        evt.stopPropagation();
        evt.preventDefault();
      }
    },
  },
  computed: {
    isCompact () {
      return this.compact !== null;
    },
    compactFormat () {
      return state.merchantConfig.widgets?.productRating?.compactFormat || 'count';
    },
    shouldShow() {
      return !!this.rating;
    },
    ratingAverage() {
      return this.rating?.ratingAverage || 0;
    },
    rating () {
      if (this.injectedRating) {
        return this.injectedRating;
      }
      if (this.ratings && this.ratingToShow && this.ratings[this.ratingToShow]) {
        return this.ratings[this.ratingToShow];
      }
      if (this.showEmptyProductScore) {
        return {
          ratingAverage: 0,
          ratingCount: 0,
        };
      }
      return null;
    },
    injectedRating () {
      if (! this.mockJson) {
        return null;
      }
      try {
        return JSON.parse(this.mockJson);
      } catch (e) {
        console.error('Failed to parse mockJson', e);
        return null;
      }
    },
    starClasses () {
      return {
        'gmf-clickable': this.popupReviewModalEnabled,
      };
    },
    popupReviewModalEnabled () {
      if (this.disableClick) {
        return false;
      }
      return state.merchantConfig.widgets?.comments?.reviewModal?.enable;
    },
    ratingCount () {
      return this.rating?.ratingCount || 0;
    },
    ratingName () {
      if (this.injectedRating) {
        return this.injectedRating.ratingName;
      }
      const key = this.ratingToShow === 'hype_factor' ? 'hypes' : 'reviews';
      if (this.ratingCount === 1) {
        const singularKey = key.substring(0, key.length - 1);
        return this.t(singularKey, true) || this.t(key); // Fallback to plural if singular is not defined in the current language
      }
      return this.t(key);
    },
    ratingToShow () {
      if (! this.ratings) {
        return 'product_score';
      }
      if (this.ratings.product_score) {
        if (this.ratings.product_score.ratingCount > 0) {
          return 'product_score';
        }
      }
      if (this.ratings.hype_factor) {
        if (this.ratings.hype_factor.ratingCount > 0) {
          return 'hype_factor';
        }
      }
      return 'product_score';
    },
    showEmptyProductScore () {
      return state.merchantConfig?.widgets.showEmptyProductScore;
    },
    ratings () {
      return combineRatings(this.allRatings);
    },
  },
  async mounted () {
    const productIds = this.productId.split('+').map(id => id.trim()).filter(id=>id);

    if (productIds.length === 0) {
      console.error('Missing product id for product-rating');
      return;
    }

    const results = await Promise.all(productIds.map(id => getProductRatings(id)));
    this.allRatings = results.filter(r=>r).map(r => r.ratings);

    this.hasLoaded = true;
  },
};
</script>
